import * as React from 'react';
import type { Store, Reducer } from 'redux';
import once from 'lodash-es/once';
import { registerReducer } from '..';

const createAsyncComponentInternal = <Props extends {} = {}, StoreRequirement = {}>(
    initializePackage: (store: Store<{}>) => Promise<React.ComponentClass<Props & {}>>
) =>
    function AsyncComponent(props: Props & StoreRequirement) {
        const stillMounted = React.useRef<boolean>();
        const [component, setComponent] = React.useState<React.ReactElement<{}> | null>(null);

        React.useEffect(() => {
            stillMounted.current = true;
            initializePackage(props['store']).then(
                Component => stillMounted.current && setComponent(<Component {...props} />)
            );

            return () => {
                stillMounted.current = false;
            };
        }, []);

        return component;
    };

export type RequirePromiseBody<TProps> = {
    packageStateKey: string;
    reducer: Reducer<{}>;
    component: React.ComponentClass<TProps & {}>;
};
export function createAsyncComponentWithReducer<Props extends {} = {}>(
    requirePromiseFactory: () => Promise<RequirePromiseBody<Props>>
) {
    return createAsyncComponentInternal<Props, { store: Store<{}> }>(
        once(
            (store: Store<{}>): Promise<React.ComponentClass<Props & {}>> =>
                requirePromiseFactory().then(({ packageStateKey, reducer, component }) => {
                    registerReducer(store, packageStateKey, reducer);
                    return component;
                })
        )
    );
}
